import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { UserPlus, ArrowRight, Flame, Award, Users, Mail, Copy } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const DEFAULT_TILES = ["🐶", "🐱", "🦊", "🐼", "🦁", "🐸", "🐵", "🐨", "🐧", "🦄", "🐙", "🐢"];

export default function Family() {
  const { user } = useAuth();
  const [kids, setKids] = useState([]);
  const [familyCode, setFamilyCode] = useState(null);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [digest, setDigest] = useState(null);
  const [tiles, setTiles] = useState(DEFAULT_TILES);
  const [form, setForm] = useState({ name: "", email: "", password: "", grade: "5", avatar: DEFAULT_TILES[0], picture_passcode: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/family/children");
      setKids(data.children);
      setFamilyCode(data.family_code);
      if (data.children.length > 0) {
        setSelected((prev) => prev || data.children[0].user_id);
      }
    } catch (err) {
      console.error("family children load failed:", err);
    }
  }, []);

  const loadDigest = useCallback(async () => {
    try {
      const r = await api.get("/family/digest/preview");
      setDigest(r.data);
    } catch (err) {
      console.error("digest load failed:", err);
    }
  }, []);

  const loadTiles = useCallback(async () => {
    try {
      const r = await api.get("/kid/tiles");
      setTiles(r.data.tiles);
    } catch (err) {
      console.error("tiles load failed:", err);
    }
  }, []);

  useEffect(() => { load(); loadTiles(); }, [load, loadTiles]);

  useEffect(() => {
    if (!selected) { setDetails(null); return; }
    let cancelled = false;
    api.get(`/family/child/${selected}/dashboard`)
      .then((r) => { if (!cancelled) setDetails(r.data); })
      .catch((err) => console.error("child dashboard load failed:", err));
    return () => { cancelled = true; };
  }, [selected]);

  useEffect(() => { if (kids.length > 0) loadDigest(); }, [kids.length, loadDigest]);

  const toggleTile = (t) => {
    setForm((f) => {
      const pc = [...(f.picture_passcode || [])];
      const idx = pc.indexOf(t);
      if (idx >= 0) pc.splice(idx, 1);
      else if (pc.length < 4) pc.push(t);
      return { ...f, picture_passcode: pc };
    });
  };

  const addChild = async (e) => {
    e.preventDefault();
    if (form.picture_passcode.length !== 4) { toast.error("Pick exactly 4 pictures for the passcode"); return; }
    setLoading(true);
    try {
      await api.post("/family/add-child", form);
      toast.success(`${form.name} added — they can now use kid mode`);
      setForm({ name: "", email: "", password: "", grade: "5", avatar: tiles[0], picture_passcode: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(familyCode);
    toast.success("Family code copied");
  };

  const totalMastery = kids.length ? Math.round(kids.reduce((a, b) => a + (b.avg_mastery || 0), 0) / kids.length) : 0;
  const totalAttempts = kids.reduce((a, b) => a + (b.attempts || 0), 0);
  const upgradePrompt = user?.plan !== "family";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="nb-badge nb-tag-coral">Family plan</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-2">Your kids' learning</h1>
          <p className="text-neutral-600 mt-1">Add each child, track mastery, celebrate wins.</p>
        </div>
        <div className="flex gap-2">
          {familyCode && (
            <button data-testid="copy-family-code" onClick={copyCode} className="nb-btn nb-btn-secondary">
              <Copy className="w-4 h-4" /> Family code: <b className="ml-1">{familyCode}</b>
            </button>
          )}
          {upgradePrompt && (
            <Link data-testid="family-upgrade" to="/pricing" className="nb-btn">
              Upgrade to Family <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <StatCard label="Kids" value={kids.length} icon={<Users />} bg="bg-white" testid="family-stat-kids" />
        <StatCard label="Total attempts" value={totalAttempts} icon={<Flame />} bg="bg-[var(--sun)]" testid="family-stat-attempts" />
        <StatCard label="Avg mastery" value={`${totalMastery}%`} icon={<Award />} bg="bg-[var(--mint)]" testid="family-stat-mastery" />
      </div>

      {/* Weekly digest */}
      {digest && digest.children.length > 0 && (
        <div className="nb-card p-6 bg-[var(--bg-highlight)]">
          <div className="flex flex-wrap justify-between items-end gap-3">
            <div>
              <span className="nb-badge nb-tag-sky"><Mail className="w-3.5 h-3.5" /> Weekly digest</span>
              <h2 className="text-2xl font-extrabold mt-2">Week of {digest.week_start}</h2>
              <p className="text-sm text-neutral-600">Preview what a Sunday parent email would show.</p>
            </div>
            <button data-testid="refresh-digest" onClick={loadDigest} className="nb-btn nb-btn-secondary text-sm">Refresh</button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {digest.children.map((c) => (
              <div key={c.child_id} data-testid={`digest-${c.child_id}`} className="p-4 border-2 border-black rounded-xl bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{c.avatar || "🧑‍🎓"}</span>
                  <div className="font-extrabold">{c.name}</div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <MiniStat label="Streak" value={`${c.streak}🔥`} />
                  <MiniStat label="Attempts" value={c.weekly_attempts} />
                  <MiniStat label="Correct" value={c.weekly_correct} />
                  <MiniStat label="Mastery" value={`${c.avg_mastery}%`} />
                </div>
                {c.goal && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs font-bold uppercase">
                      <span>Weekly goal</span>
                      <span>{c.goal.percent}%</span>
                    </div>
                    <div className="h-2 mt-1 bg-neutral-200 rounded-full border-2 border-black overflow-hidden">
                      <div className={`h-full ${c.goal.completed ? "bg-[var(--mint)]" : "bg-[var(--coral)]"}`} style={{ width: `${Math.min(100, c.goal.percent)}%` }} />
                    </div>
                  </div>
                )}
                {c.top_weak ? (
                  <div className="mt-2 text-xs">Focus next: <b>{c.top_weak.topic}</b> ({c.top_weak.mastery}%)</div>
                ) : (
                  <div className="mt-2 text-xs text-neutral-500">No practice yet this week</div>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-3">Email delivery is coming soon — add a Resend key to turn on Sunday auto-send.</p>
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-5 space-y-4">
          <div className="nb-card p-6">
            <h2 className="text-2xl font-extrabold">Add a child</h2>
            <form onSubmit={addChild} className="space-y-3 mt-4">
              <input data-testid="child-name" className="nb-input" placeholder="Child's name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input data-testid="child-email" className="nb-input" type="email" placeholder="Child's email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <input data-testid="child-password" className="nb-input" type="text" placeholder="Temporary password (backup for older kids)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
              <div className="flex gap-2">
                <select data-testid="child-grade" className="nb-input w-1/2" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}>
                  {["5", "6", "7", "8"].map((g) => {
                    const label = `Grade ${g}`;
                    return <option key={g} value={g}>{label}</option>;
                  })}
                </select>
                <select data-testid="child-avatar" className="nb-input w-1/2" value={form.avatar} onChange={(e) => setForm({ ...form, avatar: e.target.value })}>
                  {tiles.map((t) => {
                    const label = `${t} Avatar`;
                    return <option key={t} value={t}>{label}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase">Picture passcode (tap 4 in order)</label>
                <div className="mt-2 grid grid-cols-6 gap-2">
                  {tiles.map((t) => {
                    const idx = form.picture_passcode.indexOf(t);
                    return (
                      <button
                        type="button"
                        key={t}
                        data-testid={`passcode-tile-${t}`}
                        onClick={() => toggleTile(t)}
                        className={`aspect-square text-2xl border-2 border-black rounded-xl relative ${idx >= 0 ? "bg-[var(--sun)]" : "bg-white"}`}
                      >
                        {t}
                        {idx >= 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-neutral-500 mt-1">{form.picture_passcode.length}/4 picked</div>
              </div>
              <button data-testid="add-child-btn" disabled={loading} className="nb-btn w-full">
                {loading ? "Adding…" : <><UserPlus className="w-4 h-4" /> Add child</>}
              </button>
            </form>
          </div>

          <div className="nb-card p-6">
            <h2 className="text-xl font-extrabold">Your kids</h2>
            {familyCode && (
              <p className="text-sm text-neutral-600 mt-1">Kid login? Share code <b>{familyCode}</b> — kids sign in at <Link to="/kids" className="underline font-bold">/kids</Link></p>
            )}
            <div className="mt-3 space-y-2">
              {kids.length === 0 && <div className="text-sm text-neutral-500">No kids added yet.</div>}
              {kids.map((k) => (
                <button
                  key={k.user_id}
                  data-testid={`child-${k.user_id}`}
                  onClick={() => setSelected(k.user_id)}
                  className={`w-full text-left p-3 border-2 border-black rounded-xl flex justify-between items-center ${selected === k.user_id ? "bg-[var(--coral)] text-white" : "bg-white"}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{k.avatar || "🧑‍🎓"}</span>
                    <div>
                      <div className="font-extrabold">{k.name}</div>
                      <div className="text-xs opacity-80">{k.grade ? `Grade ${k.grade} · ` : ""}{k.email}</div>
                    </div>
                  </div>
                  <div className="text-right text-xs font-bold">
                    <div>{k.avg_mastery}% mastery</div>
                    <div>{k.streak_days}🔥 · {k.badges_earned}🏆</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          {details ? (
            <div className="nb-card p-6 space-y-4">
              <div className="flex justify-between items-end">
                <div>
                  <div className="text-sm uppercase font-bold text-neutral-500">Detail view</div>
                  <h2 className="text-3xl font-extrabold">{details.child.name}</h2>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-extrabold">{details.avg_mastery}%</div>
                  <div className="text-xs uppercase font-bold">mastery</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <MiniStat label="Attempts" value={details.total_attempts} />
                <MiniStat label="Correct" value={details.correct_attempts} />
                <MiniStat label="Streak" value={`${details.streak_days}🔥`} />
              </div>
              <div>
                <h3 className="font-extrabold mt-3">Weak points</h3>
                {details.weak_points.length === 0 ? (
                  <div className="text-sm text-neutral-500">No weak spots — nice!</div>
                ) : (
                  <div className="space-y-2 mt-2">
                    {details.weak_points.map((w) => (
                      <div key={w.topic} className="flex justify-between items-center p-3 border-2 border-black rounded-xl bg-[var(--bg-highlight)]">
                        <div>
                          <div className="text-xs uppercase font-bold text-neutral-500">{w.subject}</div>
                          <div className="font-extrabold">{w.topic}</div>
                        </div>
                        <span className="nb-badge nb-tag-coral">{w.mastery}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="nb-card p-10 text-center bg-[var(--bg-highlight)]">
              <Users className="w-10 h-10 mx-auto text-[var(--coral)]" />
              <h3 className="text-2xl font-extrabold mt-2">Pick a child to see their progress</h3>
              <p className="text-sm text-neutral-600">Or add one on the left.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, bg, testid }) {
  return (
    <div data-testid={testid} className={`nb-card p-5 ${bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-bold text-neutral-600">{label}</div>
        <div className="w-8 h-8 rounded-lg border-2 border-black flex items-center justify-center bg-white">{icon}</div>
      </div>
      <div className="text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="p-3 border-2 border-black rounded-xl bg-white text-center">
      <div className="text-2xl font-extrabold">{value}</div>
      <div className="text-[11px] uppercase font-bold text-neutral-500">{label}</div>
    </div>
  );
}
